<template>
  <div class="space-y-1">
    <!-- Label -->
    <label 
      v-if="label" 
      class="block text-sm font-medium text-slate-700 dark:text-slate-300"
    >
      {{ label }}
    </label>

    <!-- Input Container -->
    <div class="relative">
      <!-- Icon Support (Left) -->
      <div 
        v-if="$slots['icon-left']" 
        class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400"
      >
        <slot name="icon-left"></slot>
      </div>

      <!-- Input Element -->
      <component
        :is="isTextarea ? 'textarea' : 'input'"
        :value="modelValue"
        :type="!isTextarea ? type : undefined"
        :rows="isTextarea ? rows : undefined"
        :placeholder="placeholder"
        :disabled="disabled"
        @input="$emit('update:modelValue', $event.target.value)"
        @blur="$emit('blur', $event)"
        @focus="$emit('focus', $event)"
        @keydown="$emit('keydown', $event)"
        class="block w-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed outline-none resize-none"
        :class="[
          !isTextarea && $slots['icon-left'] ? (size === 'sm' ? 'pl-8' : 'pl-10') : (size === 'sm' ? 'pl-3' : 'pl-4'),
          !isTextarea && $slots['icon-right'] ? (size === 'sm' ? 'pr-8' : 'pr-10') : (size === 'sm' ? 'pr-3' : 'pr-4'),
          error ? 'border-red-300 text-red-900 placeholder-red-300 focus:border-red-500 focus:ring-red-500 dark:border-red-800 dark:text-red-400' : 'border-transparent',
          
          // Sizing & Variants
          variant === 'soft' 
            ? `bg-gray-50 border-none rounded-2xl font-bold dark:bg-slate-800 dark:text-slate-100 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 ${size === 'sm' ? 'text-xs p-2' : 'text-sm p-3'}`
            : variant === 'ghost'
              ? `bg-transparent border-none focus:ring-0 font-bold dark:text-slate-100 p-0 ${size === 'sm' ? 'text-xs' : 'text-sm'}`
              : `bg-white rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-slate-900 dark:text-white dark:disabled:bg-slate-800 ${size === 'sm' ? 'text-xs py-2' : 'sm:text-sm py-2.5'}`,
          
          isTextarea ? 'min-h-[80px]' : ''
        ]"
      />

      <!-- Icon Support (Right) -->
      <div 
        v-if="!isTextarea && $slots['icon-right'] && !error" 
        class="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400"
      >
         <slot name="icon-right"></slot>
      </div>

      <!-- Error Icon (Overrides right icon if error exists) -->
      <div 
        v-if="!isTextarea && error" 
        class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
      >
        <svg class="h-5 w-5 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
        </svg>
      </div>
    </div>

    <!-- Helper Text or Error Message -->
    <p 
      v-if="error" 
      class="mt-1 text-sm text-red-600 dark:text-red-400"
    >
      {{ error }}
    </p>
    <p 
      v-else-if="helperText" 
      class="mt-1 text-xs text-slate-500 dark:text-slate-400"
    >
      {{ helperText }}
    </p>
  </div>
</template>

<script setup>
defineProps({
  modelValue: {
    type: [String, Number],
    default: ''
  },
  label: {
    type: String,
    default: ''
  },
  type: {
    type: String,
    default: 'text'
  },
  placeholder: {
    type: String,
    default: ''
  },
  error: {
    type: String,
    default: ''
  },
  helperText: {
    type: String,
    default: ''
  },
  size: {
    type: String,
    default: 'md' // 'sm' | 'md' | 'lg'
  },
  variant: {
    type: String,
    default: 'bordered', // 'bordered' | 'soft' | 'ghost'
    validator: (val) => ['bordered', 'soft', 'ghost'].includes(val)
  },
  isTextarea: {
    type: Boolean,
    default: false
  },
  rows: {
    type: [Number, String],
    default: 3
  }
});

defineEmits(['update:modelValue', 'blur', 'focus', 'keydown']);
</script>

<style scoped>
/* Optional: Ensure basic input reset if not covered by main css */
input, textarea {
  padding-top: 0.625rem; /* ~10px */
  padding-bottom: 0.625rem;
}
input.py-2, textarea.py-2 {
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
}
</style>
